// Location detail page. Fetches the row + child access points, then renders
// the metadata grid + APs sub-section. AP rows link to /access-points/:id;
// "Add AP" links to /access-points/add?parentLocationId=:id.

import React, {useCallback, useEffect, useState} from 'react'
import clsx from 'clsx'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {MapPin, Plus, Unlink, Wifi} from 'lucide-react'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {showToast} from '../../components/common/Toast'
import {supabase} from '../../app/supabaseClient'
import type {APDetail, APStatus, LocationDetail, LocationPriority} from './types'

const PRIORITY_BADGE: Record<LocationPriority, string> = {
  CRITICAL: 'bg-badgeRedBg    text-statusRed',
  HIGH:     'bg-badgeOrangeBg text-statusOrange',
  MEDIUM:   'bg-badgeYellowBg text-statusYellow',
}

const AP_STATUS_DOT: Record<APStatus, string> = {
  active:  'bg-statusGreen',
  offline: 'bg-textMuted',
  fault:   'bg-statusRed',
}

export const LocationDetailScreen: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [loc, setLoc] = useState<LocationDetail | null>(null)
  const [aps, setAPs] = useState<APDetail[]>([])
  const [unassignedAPs, setUnassignedAPs] = useState<APDetail[]>([])
  const [assigning, setAssigning] = useState(false)
  const [pickedAPId, setPickedAPId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Reload both this location's APs AND the unassigned-AP picker list.
  // Used after assign/unassign mutations to keep both lists in sync.
  const refreshAPs = useCallback(async () => {
    if (!id) return
    const [{data: ownRows}, {data: orphanRows}] = await Promise.all([
      supabase
        .from('access_points')
        .select(
          'id, location_id, name, mac_address, ssid, ip_address, is_active, status, ' +
            'x, y, z, level, radius_m',
        )
        .eq('location_id', id)
        .order('name'),
      // Orphans = APs with location_id IS NULL. Available to assign to this
      // location without first re-parenting via the AP edit screen.
      supabase
        .from('access_points')
        .select(
          'id, location_id, name, mac_address, ssid, ip_address, is_active, status, ' +
            'x, y, z, level, radius_m',
        )
        .is('location_id', null)
        .order('name'),
    ])
    setAPs((ownRows as unknown as APDetail[]) ?? [])
    setUnassignedAPs((orphanRows as unknown as APDetail[]) ?? [])
  }, [id])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data: l, error: le} = await supabase
        .from('locations')
        .select(
          'id, name, zone, level, type, description, priority, material, heading_status, ' +
            'next_blast_date, tonnes_remaining, x, y, z',
        )
        .eq('id', id)
        .single()
      if (cancelled) return
      if (le) {
        setError(le.message)
        setLoading(false)
        return
      }
      setLoc(l as unknown as LocationDetail)
      await refreshAPs()
      if (cancelled) return
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, refreshAPs])

  const handleAssign = async () => {
    if (!id || !pickedAPId) return
    setAssigning(true)
    const {error: e} = await supabase
      .from('access_points')
      .update({location_id: id, updated_at: new Date().toISOString()})
      .eq('id', pickedAPId)
    setAssigning(false)
    if (e) {
      showToast(`Assign failed: ${e.message}`, 'error')
      return
    }
    showToast('Access point assigned.', 'success')
    setPickedAPId('')
    void refreshAPs()
  }

  const handleUnassign = async (apId: string, apName: string) => {
    if (!window.confirm(`Unassign "${apName}" from this location?`)) return
    const {error: e} = await supabase
      .from('access_points')
      .update({location_id: null, updated_at: new Date().toISOString()})
      .eq('id', apId)
    if (e) {
      showToast(`Unassign failed: ${e.message}`, 'error')
      return
    }
    showToast('Access point unassigned.', 'success')
    void refreshAPs()
  }

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={loc?.name ?? 'Location'} backTo="/locations" />
      <PageShell
        footer={
          loc && (
            <button
              type="button"
              onClick={() => navigate(`/locations/${loc.id}/edit`)}
              className="rounded-md border border-primary px-lg py-sm text-md font-semibold text-primary hover:bg-primaryBg"
            >
              Edit
            </button>
          )
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : error || !loc ? (
          <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
            <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
            <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{error ?? 'Not found'}</pre>
          </div>
        ) : (
          <div className="flex flex-col gap-xl">
            {/* Header — icon + name + zone + priority chip */}
            <div className="flex items-center gap-lg">
              <div className="flex h-14 w-14 items-center justify-center rounded-md bg-surfaceLight">
                <MapPin size={28} color="var(--color-primary)" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xxl font-bold text-textPrimary">{loc.name}</div>
                <div className="text-md text-textMuted">{loc.zone ?? '—'}</div>
              </div>
              {loc.priority && (
                <span
                  className={clsx(
                    'rounded-sm px-sm py-xs text-xs font-bold uppercase',
                    PRIORITY_BADGE[loc.priority],
                  )}
                >
                  {loc.priority}
                </span>
              )}
            </div>

            {/* Field grid */}
            <dl className="grid grid-cols-3 gap-x-xl gap-y-md rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <ViewField label="Type" value={loc.type ?? '—'} />
              <ViewField label="Material" value={loc.material ?? '—'} />
              <ViewField label="Heading status" value={loc.heading_status ?? '—'} />
              <ViewField
                label="Tonnes remaining"
                value={loc.tonnes_remaining != null ? loc.tonnes_remaining.toLocaleString() : '—'}
              />
              <ViewField label="Next blast" value={loc.next_blast_date ?? '—'} />
              <ViewField label="Level" value={loc.level != null ? `Level ${loc.level}` : '—'} />
              <ViewField label="X" value={loc.x != null ? loc.x.toFixed(2) : '—'} />
              <ViewField label="Y" value={loc.y != null ? loc.y.toFixed(2) : '—'} />
              <ViewField label="Z" value={loc.z != null ? loc.z.toFixed(2) : '—'} />
              {loc.description && (
                <div className="col-span-3">
                  <dt className="text-xs uppercase tracking-wider text-textMuted">Description</dt>
                  <dd className="mt-1 text-md text-textPrimary">{loc.description}</dd>
                </div>
              )}
            </dl>

            {/* Access Points */}
            <div className="rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <div className="mb-md flex items-center justify-between">
                <div className="text-sm font-semibold uppercase tracking-wider text-textMuted">
                  Access Points ({aps.length})
                </div>
                <Link
                  to={`/access-points/add?parentLocationId=${loc.id}`}
                  className="flex items-center gap-sm rounded-md border border-primary px-md py-xs text-sm font-semibold text-primary hover:bg-primaryBg"
                >
                  <Plus size={14} color="var(--color-primary)" strokeWidth={2.5} />
                  Add AP
                </Link>
              </div>

              {/* Assign an existing unassigned AP. Visible whenever orphan
                  APs exist. Reassignment-from-another-location is done via
                  the AP edit screen's Parent Location dropdown — keeps this
                  surface simple. */}
              {unassignedAPs.length > 0 && (
                <div className="mb-lg flex flex-wrap items-center gap-md rounded-md bg-surface px-md py-sm">
                  <span className="text-xs uppercase tracking-wider text-textMuted">
                    Assign existing
                  </span>
                  <select
                    value={pickedAPId}
                    onChange={ev => setPickedAPId(ev.target.value)}
                    className="flex-1 min-w-[180px] rounded-md border border-surfaceBorder bg-surfaceCard px-md py-xs text-md text-textPrimary"
                  >
                    <option value="">Pick an unassigned AP…</option>
                    {unassignedAPs.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                        {a.mac_address ? ` (${a.mac_address})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAssign}
                    disabled={!pickedAPId || assigning}
                    className="rounded-md bg-primary px-md py-xs text-sm font-bold text-black hover:bg-primaryLight disabled:opacity-40"
                  >
                    {assigning ? 'Assigning…' : 'Assign'}
                  </button>
                </div>
              )}

              {aps.length === 0 ? (
                <div className="text-sm text-textMuted">
                  No access points installed at this location yet.
                </div>
              ) : (
                <ul className="flex flex-col gap-sm">
                  {aps.map(ap => (
                    <li key={ap.id} className="flex items-center gap-sm rounded-md bg-surface px-md py-sm">
                      <Link
                        to={`/access-points/${ap.id}`}
                        className="flex flex-1 items-center gap-md text-left hover:opacity-80"
                      >
                        <Wifi size={16} color="var(--color-textSecondary)" />
                        <span className={clsx('h-2 w-2 rounded-full', AP_STATUS_DOT[ap.status])} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-md text-textPrimary">{ap.name}</div>
                          <div className="truncate text-xs text-textMuted">
                            {ap.ssid ?? '—'} · {ap.mac_address ?? '—'}
                          </div>
                        </div>
                        <span className="text-xs uppercase text-textMuted">{ap.status}</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleUnassign(ap.id, ap.name)}
                        title="Unassign from this location"
                        aria-label={`Unassign ${ap.name}`}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-surfaceBorder text-textSecondary hover:border-statusRed hover:text-statusRed"
                      >
                        <Unlink size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </PageShell>
    </main>
  )
}

const ViewField: React.FC<{label: string; value: string}> = ({label, value}) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-textMuted">{label}</dt>
    <dd className="mt-1 text-md text-textPrimary">{value}</dd>
  </div>
)
